/**
 * Keyword Analyzer
 * Evaluates focus keyword usage across the blog post.
 */

// Helper to check if a word/phrase exists in text (case insensitive)
const containsKeyword = (text, keyword) => {
  if (!text || !keyword) return false;
  return text.toLowerCase().includes(keyword.toLowerCase());
};

// Helper to count keyword occurrences
const countKeywordOccurrences = (text, keyword) => {
  if (!text || !keyword) return 0;
  const regex = new RegExp(keyword.toLowerCase(), 'g');
  const matches = text.toLowerCase().match(regex);
  return matches ? matches.length : 0;
};

// Helper to get word count
const getWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

// Extract HTML text content (strip tags)
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, ' ');
};

export const analyzeKeyword = (data) => {
  const { 
    title = "", 
    slug = "", 
    metaTitle = "", 
    metaDescription = "", 
    content = "", 
    focusKeyword = "" 
  } = data;

  let score = 0;
  let maxScore = 35; // Total points possible for keyword
  const results = [];
  const keyword = (focusKeyword || "").trim();

  if (!keyword) {
    return {
      score: 0,
      maxScore,
      results: [{ status: "error", text: "No focus keyword defined." }]
    };
  }

  // 1. Keyword in Title
  if (containsKeyword(title, keyword)) {
    score += 5;
    results.push({ status: "success", text: "Focus keyword appears in the blog title." });
  } else {
    results.push({ status: "error", text: "Focus keyword is missing from the blog title." });
  }

  // 2. Keyword in Meta Title
  if (containsKeyword(metaTitle, keyword)) {
    score += 5;
    results.push({ status: "success", text: "Focus keyword appears in the meta title." });
  } else {
    results.push({ status: "warning", text: "Focus keyword is missing from the meta title." });
  }

  // 3. Keyword in Meta Description
  if (containsKeyword(metaDescription, keyword)) {
    score += 5;
    results.push({ status: "success", text: "Focus keyword appears in the meta description." });
  } else {
    results.push({ status: "error", text: "Focus keyword is missing from the meta description." });
  }

  // 4. Keyword in Slug
  if (containsKeyword(slug, keyword.replace(/\s+/g, '-'))) {
    score += 5;
    results.push({ status: "success", text: "Focus keyword appears in the URL slug." });
  } else {
    results.push({ status: "warning", text: "Focus keyword is missing from the URL slug." });
  }

  // 5. Keyword in first 100 words of content
  const cleanContent = stripHtml(content);
  const first100Words = cleanContent.split(/\s+/).slice(0, 100).join(" ");
  if (containsKeyword(first100Words, keyword)) {
    score += 5;
    results.push({ status: "success", text: "Focus keyword appears in the first 100 words of the content." });
  } else {
    results.push({ status: "warning", text: "Focus keyword is missing from the beginning of your content." });
  }

  // 6. Keyword Density (0.8% - 2.0%)
  const wordCount = getWordCount(cleanContent);
  if (wordCount > 0) {
    const keywordCount = countKeywordOccurrences(cleanContent, keyword);
    const density = (keywordCount / wordCount) * 100;
    
    if (density >= 0.8 && density <= 2.5) {
      score += 5;
      results.push({ status: "success", text: `Keyword density is optimal (${density.toFixed(1)}%).` });
    } else if (density > 2.5) {
      results.push({ status: "warning", text: `Keyword density is high (${density.toFixed(1)}%). Avoid keyword stuffing.` });
    } else if (keywordCount > 0) {
      results.push({ status: "warning", text: `Keyword density is low (${density.toFixed(1)}%). Try using it more naturally.` });
    } else {
      results.push({ status: "error", text: "Focus keyword does not appear in the content." });
    }
  }

  // 7. Keyword in H2 / H3 / Image Alt
  const hasHeadingKeyword = /<h[23][^>]*>.*?<\/h[23]>/gi.test(content) && containsKeyword(content.match(/<h[23][^>]*>.*?<\/h[23]>/gi)?.join(" ") || "", keyword);
  if (hasHeadingKeyword) {
    score += 5;
    results.push({ status: "success", text: "Focus keyword appears in at least one subheading (H2/H3)." });
  } else {
    results.push({ status: "warning", text: "Focus keyword is not used in any subheadings." });
  }

  return { score, maxScore, results };
};
